import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaStar, FaCheckCircle, FaMoneyBillWave, FaSmile, FaHourglassHalf } from "react-icons/fa";
import { Button } from "../../../../ui/button";
import { Textarea } from "../../../../ui/textarea";
import { Card, CardContent } from "../../../../ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../../ui/dialog";
import { feedbackByHouseOwner } from "../../../../../apiManager/houseOwner/matchShvoller";
import { formatPayment } from "../../../../../lib/utils";

export function ServiceFinishedViewByOwner({ job }) {
  const navigate = useNavigate();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [review, setReview] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // State for the validation modal
  const [showValidationDialog, setShowValidationDialog] = useState(false);

  const remainingPayment = formatPayment(job.paymentInfo?.amount || 0, {
    is80: true,
    isVolunteer: job.isRequestedForVolunteer,
  });

  const handleSubmit = async () => {
    if (!job?._id) return;

    // Validation: Rating is required
    if (rating === 0) {
      setShowValidationDialog(true);
      return;
    }

    setIsLoading(true);
    try {
      await feedbackByHouseOwner(job._id, rating, review);
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setIsLoading(false);
      window.location.reload();
    }
  };

  const handleRatingClick = (rate) => setRating(rate);

  // Check if feedback already exists
  const feedbackExists = job.jobRating && job.houseOwnerFeedback;

  // Check shoveller action
  const shovellerAction = job.ShovelerInfo?.[0]?.shovellerAction;

  return (
    <div className="flex flex-col items-center text-center py-10 px-4 lg:px-20 space-y-8">
      {/* Header */}
      <div className="flex flex-col items-center space-y-4">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center shadow-md">
          <FaCheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <div className="space-y-2">
          <h2 className="font-bold text-2xl text-gray-900">Service Completed</h2>
          <p className="text-gray-700 text-lg leading-relaxed max-w-3xl">
            Thank you for working with your helper! Please share your experience.
          </p>
        </div>
      </div>

      {/* Payment Info Card */}
      <Card className="w-full max-w-3xl border-none shadow-md rounded-lg">
        <CardContent className="pt-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <FaMoneyBillWave className="w-6 h-6 text-blue-600" />
            </div>
            <div className="text-left">
              <p className="text-2xl font-bold text-gray-900">${remainingPayment}</p>
              <p className="text-xs text-gray-500 mt-1">
                Payment will be finalized upon job completion confirmation from the shoveller.
              </p>
            </div>
          </div>
          <div className="hidden md:flex flex-col items-center text-green-600">
            <FaSmile className="w-12 h-12 mb-2" />
            <p className="text-sm font-medium">Everyone's happy!</p>
          </div>
        </CardContent>
      </Card>

      {/* Conditional Feedback / Status */}
      {!feedbackExists ? (
        <>
          {/* Rating Section */}
          <Card className="w-full max-w-3xl border-none shadow-md rounded-lg">
            <CardContent className="pt-6 space-y-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-left">
                  <p className="text-lg font-medium text-gray-700">Rate Your Shoveller</p>
                  <p className="text-sm text-gray-500">Rating is required</p>
                </div>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <FaStar
                      key={star}
                      className={`cursor-pointer text-3xl transition-colors ${
                        rating >= star || hoveredRating >= star
                          ? "text-yellow-400"
                          : "text-gray-300"
                      }`}
                      onClick={() => handleRatingClick(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(0)}
                    />
                  ))}
                </div>
              </div>
              <div className="flex flex-col">
                <Textarea
                  value={review}
                  onChange={(e) => setReview(e.target.value)}
                  placeholder="Share your experience (Optional - max 100 characters)"
                  maxLength={100}
                  className="min-h-[120px] resize-none"
                />
                <div className="text-xs text-gray-500 text-right mt-1">
                  {review.length}/100
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Button */}
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            className="w-full max-w-xl text-lg py-4"
            size="lg"
          >
            {isLoading ? "Submitting..." : "Continue"}
          </Button>

          {/* Validation Dialog */}
          <Dialog open={showValidationDialog} onOpenChange={setShowValidationDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Rating Required</DialogTitle>
                <DialogDescription>
                  Please select a star rating for your shoveller to proceed.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="sm:justify-end">
                <Button type="button" onClick={() => setShowValidationDialog(false)}>
                  Okay
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <Card className="w-full max-w-3xl border-none shadow-md rounded-lg">
          <CardContent className="pt-6 flex flex-col items-center space-y-4">
            {shovellerAction === "accepted" && (
              <>
                <FaHourglassHalf className="w-12 h-12 text-yellow-500" />
                <p className="text-lg font-medium text-gray-700">
                  Your feedback has been submitted. Please wait for confirmation from your shoveller.
                </p>
              </>
            )}
            {shovellerAction === "completed" && (
              <>
                <FaCheckCircle className="w-12 h-12 text-green-600" />
                <p className="text-lg font-medium text-gray-700">
                  Congratulations! This job has been completed and accepted by your shoveller.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}